import MyLocationIcon from '@mui/icons-material/MyLocation';
import { formatDateTime, type LocationPing } from '@fdt/shared';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, errorKey } from '../api/client';
import { LiveMap } from '../components/LiveMap';
import { PageHeader } from '../components/PageHeader';
import { currentLocale } from '../i18n';
import { trackingApi } from '../resources/tracking';

/** How often the map asks again. Every call is logged, so not more often. */
const REFRESH_MS = 20_000;

export function LiveMapPage() {
  const { t } = useTranslation();
  const locale = currentLocale();

  const settings = useQuery({
    queryKey: ['settings', 'tiles'],
    queryFn: async () => (await api.get<{ osmTileUrl: string }>('/settings')).data,
    staleTime: 5 * 60_000,
  });

  const positions = useQuery({
    queryKey: ['tracking', 'live'],
    queryFn: () => trackingApi.live(),
    refetchInterval: REFRESH_MS,
  });

  if (positions.isPending) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (positions.isError) {
    return <Alert severity="error">{t(errorKey(positions.error))}</Alert>;
  }

  const rows = positions.data ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <PageHeader icon={MyLocationIcon} tone="accent" title={t('tracking.liveTitle')} />
        <Typography variant="body2" color="text.secondary">
          {t('tracking.liveHint')}
        </Typography>
      </Stack>

      <LiveMap
        positions={rows}
        tileUrl={settings.data?.osmTileUrl ?? 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'}
      />

      <Card>
        <CardContent>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('tracking.liveEmpty')}
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {rows.map((ping) => (
                <LiveRow key={ping.id} ping={ping} locale={locale} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

function LiveRow({ ping, locale }: { ping: LocationPing; locale: 'it' | 'tr' }) {
  const { t } = useTranslation();
  const ageMinutes = Math.round((Date.now() - Date.parse(ping.recordedAt)) / 60_000);

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ py: 1.25 }}
    >
      <Typography sx={{ fontWeight: 600, minWidth: 180 }}>{ping.userName ?? '—'}</Typography>
      {ping.vehiclePlate && <Chip size="small" label={ping.vehiclePlate} />}
      <Chip
        size="small"
        variant="outlined"
        label={t(`tracking.phases.${ping.phase}`)}
        color={ping.phase === 'on_route' ? 'primary' : 'default'}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="body2" color="text.secondary">
        {t('tracking.lastSeen')}: {formatDateTime(ping.recordedAt, locale)}
      </Typography>
      {/* Age matters more than the timestamp: a map of positions from an hour
          ago is not a live map, and the reader should be able to see that. */}
      <Chip
        size="small"
        label={`${ageMinutes} ${t('routes.minutes')}`}
        color={ageMinutes > 10 ? 'warning' : 'success'}
      />
      {ping.accuracyMeters !== null && (
        <Typography variant="caption" color="text.secondary">
          ±{Math.round(ping.accuracyMeters)} m
        </Typography>
      )}
    </Stack>
  );
}
